// What a file sheet does, for the desktop drawer and the phone sheet alike: open (at a page), download, upload a new
// version, link to another folder, remove from this folder, edit or rewrite the summary, describe an unreadable file
// by hand, and restore an older version. Each says what happened in a toast.
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { can, type FileDetail, type Folder, type SessionUser } from '@dpw/shared';
import { ApiError } from '@dpw/shared';
import { api } from '../api';
import { download } from '../ui/FileList';
import { useToast } from '../ui/toast';
import { useFile, useFolders, uploadVersion } from './hooks';
import { fromForm, kindOf, keyPointAt, type SummaryForm } from './model';

export function useFileSheet({ fileId, folder, me, page, kp, onClose }: {
  fileId: string; folder: Folder | null; me: SessionUser; page?: number | null; kp?: number | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rw, setRw] = useState<string | null>(null);
  const f = useFile(fileId, rw !== null);
  const folders = useFolders();
  const [editing, setEditing] = useState(false);
  const [confirmRw, setConfirmRw] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [rmAsk, setRmAsk] = useState(false);
  const [vConfirm, setVConfirm] = useState(0);
  const [busy, setBusy] = useState(false);
  const d: FileDetail | undefined = f.data;
  const writer = can(me.role, 'file.write');
  const refresh = () => qc.invalidateQueries();
  const fail = (e: unknown) => toast(e instanceof Error ? e.message : 'That did not work.');

  // A rewrite is done when the summary's time changes.
  useEffect(() => {
    if (rw !== null && d?.summary && d.summary.generatedAt !== rw && d.summary.author === 'ai') { setRw(null); toast('Summary rewritten from the file.'); }
  }, [d?.summary?.generatedAt, rw]);
  // A version being read: say so when it is ready.
  const wasReading = useRef(false);
  useEffect(() => {
    if (!d) return;
    if (d.status === 'reading') wasReading.current = true;
    else if (wasReading.current) { wasReading.current = false; toast(d.version > 1 ? `Version ${d.version} is ready. The summary now says what changed.` : `Summary ready: ${d.name}`); }
  }, [d?.status]);

  const hl = kp ?? keyPointAt(d?.summary?.summary, page);
  const short = d ? d.name.replace(/\.[a-z0-9]+$/i, '') : '';
  const others = (folders.data ?? []).filter(x => x.canAdd && x.id !== folder?.id && !d?.alsoIn.some(l => l.kind === 'folder' && l.id === x.id));
  const canRemove = !!folder && writer && (folder.canEdit || d?.addedBy?.id === me.id);
  const elsewhere = (d?.alsoIn ?? []).filter(l => !(l.kind === 'folder' && l.id === folder?.id));

  const openAt = (ref?: number | null) => {
    if (!d) return;
    const pdf = kindOf(d.name) === 'pdf' && ref ? `#page=${ref}` : '';
    window.open(api.knowledge.openUrl(d.id) + pdf, '_blank', 'noopener');
  };
  const pick = (then: (file: File) => void) => {
    const i = document.createElement('input');
    i.type = 'file';
    i.onchange = () => { const x = i.files?.[0]; if (x) then(x); };
    i.click();
  };
  const newVersion = () => pick(file => {
    if (!d) return;
    toast(d.status === 'unreadable' ? `Uploading an unlocked copy as version ${d.version + 1}.` : `Uploading version ${d.version + 1}. The assistant compares it with v${d.version}.`);
    uploadVersion(d.id, file).then(() => refresh(), fail);
  });
  const addTo = (x: Folder) => {
    if (!d) return;
    api.knowledge.addToFolder(d.id, x.id).then(() => { setAddOpen(false); void refresh(); toast(`Linked to ${x.name}. It is still one file.`); }, fail);
  };
  const remove = () => {
    if (!d || !folder) return;
    api.knowledge.removeFromFolder(d.id, folder.id).then(() => { setRmAsk(false); onClose(); void refresh(); toast(`Removed from ${folder.name}.`); }, fail);
  };
  const rmText = !folder ? '' : elsewhere.length
    ? `Remove it from ${folder.name}? It stays linked to ${elsewhere.map(l => l.name).join(' and ')}.`
    : `Remove it from ${folder.name}? It is in no other place, so it is removed too (kept 30 days, then deleted).`;
  const saveSummary = (form: SummaryForm) => {
    if (!d) return;
    const s = fromForm(form);
    if (!s.whatItIs) { toast('Say what the file is in a sentence.'); return; }
    setBusy(true);
    api.knowledge.editSummary(d.id, s).then(() => { setEditing(false); void refresh(); toast('Summary saved. The assistant uses your version from now on.'); }, fail).finally(() => setBusy(false));
  };
  const saveDescription = (text: string) => {
    if (!d) return;
    const v = text.trim();
    if (!v) { toast('Write a sentence or two about what is in it.'); return; }
    api.knowledge.editSummary(d.id, { whatItIs: v, keyPoints: [], dates: [], peopleOrgs: [], numbers: [], length: 'Unknown', language: 'Not read, described by hand' })
      .then(() => { void refresh(); toast('Description saved. The assistant uses it to decide when this file matters.'); }, fail);
  };
  const rewrite = (confirm = false) => {
    if (!d) return;
    const before = d.summary?.generatedAt ?? '';
    api.knowledge.rewriteSummary(d.id, confirm).then(() => { setConfirmRw(false); setEditing(false); setRw(before); }, e => {
      if (e instanceof ApiError && e.code === 'confirm_rewrite') setConfirmRw(true); else fail(e);
    });
  };
  const restore = (v: number) => {
    if (!d) return;
    api.knowledge.restoreVersion(d.id, v).then(r => { setVConfirm(0); void refresh(); toast(`Restored v${v} as version ${r.version}. The summary is being rewritten.`); }, fail);
  };
  const openVersion = (v: number) => { if (d) window.open(api.knowledge.versionUrl(d.id, v), '_blank', 'noopener'); };

  return {
    f, d, hl, short, writer, others, canRemove, elsewhere, rmText, busy,
    editing, setEditing, confirmRw, setConfirmRw, addOpen, setAddOpen, rmAsk, setRmAsk, vConfirm, setVConfirm,
    rewriting: rw !== null,
    openAt, download: () => { if (d) void download(d).catch(fail); }, newVersion, addTo, remove, saveSummary, saveDescription, rewrite, restore, openVersion,
  };
}
